import React, { useRef, useEffect, useState, forwardRef } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import {
    useFloating,
    offset,
    flip,
    shift,
    autoUpdate,
    type Placement,
} from '@floating-ui/react'

const PopoutContent = forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement>
>((props, ref) => {
    const { popover, ...safeProps } = props
    return <StyledPopout ref={ref} {...safeProps} />
})

const StyledPopout = styled.div`
    background: ${({ theme }) => theme?.colors?.background?.bg1 || '#ffffff'};
    border: 1px solid
        ${({ theme }) => theme?.colors?.greyScale[200] || '#e0e0e0'};
    border-radius: ${({ theme }) => theme?.borderRadius?.md || '0.375rem'};
    box-shadow:
        0 4px 6px -1px rgba(0, 0, 0, 0.1),
        0 2px 4px -1px rgba(0, 0, 0, 0.06);
    z-index: 1001;
    min-width: 200px;
    padding: ${({ theme }) => theme?.spacing?.[2] || '0.5rem'};
`

export interface FloatingPopoutProps {
    trigger: React.ReactNode
    content: React.ReactNode
    open?: boolean
    onOpenChange?: (open: boolean) => void
    placement?: Placement
    offset?: number
    className?: string
    style?: React.CSSProperties
}

export const FloatingPopout: React.FC<FloatingPopoutProps> = ({
    trigger,
    content,
    open: controlledOpen,
    onOpenChange,
    placement = 'bottom-start',
    offset: offsetValue = 4,
    className,
    style,
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const finalOpen = controlledOpen !== undefined ? controlledOpen : isOpen

    const referenceRef = useRef<HTMLDivElement>(null)

    const { refs, floatingStyles } = useFloating({
        open: finalOpen,
        onOpenChange: (open) => {
            if (controlledOpen === undefined) {
                setIsOpen(open)
            }
            onOpenChange?.(open)
        },
        placement,
        strategy: 'fixed',
        middleware: [offset(offsetValue), flip(), shift({ padding: 5 })],
        whileElementsMounted: autoUpdate,
    })

    useEffect(() => {
        if (!finalOpen) return

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node
            const referenceElement = refs.reference.current
            const floatingElement = refs.floating.current

            const isInsideReference =
                referenceElement &&
                'contains' in referenceElement &&
                referenceElement.contains(target)
            const isInsideFloating =
                floatingElement &&
                'contains' in floatingElement &&
                floatingElement.contains(target)

            if (isInsideReference || isInsideFloating) {
                return
            }

            // Check if target is inside a menu (for nested dropdowns)
            let element = target as Element | null
            while (element) {
                if (
                    element.getAttribute('role') === 'menu' ||
                    element.getAttribute('role') === 'menuitem' ||
                    element.getAttribute('data-baseui-menu') === 'true'
                ) {
                    return
                }
                element = element.parentElement
            }

            // Check if target is a text field
            const isTextInput =
                (target instanceof HTMLInputElement &&
                    [
                        'text',
                        'password',
                        'email',
                        'search',
                        'tel',
                        'url',
                        'number',
                    ].includes(target.type)) ||
                target instanceof HTMLTextAreaElement

            // Close popout for outside clicks
            onOpenChange?.(false)
            if (controlledOpen === undefined) {
                setIsOpen(false)
            }

            if (!isTextInput) {
                // For non-text outside clicks: prevent underlying click
                event.preventDefault()
                event.stopPropagation()
            }
            // For text inputs: let the event proceed to focus the field
        }

        document.addEventListener('click', handleClickOutside, {
            capture: true,
        })
        return () => {
            document.removeEventListener('click', handleClickOutside, {
                capture: true,
            })
        }
    }, [finalOpen, onOpenChange, controlledOpen, refs])

    useEffect(() => {
        if (!finalOpen) return

        const handleEsc = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onOpenChange?.(false)
                if (controlledOpen === undefined) {
                    setIsOpen(false)
                }
            }
        }

        document.addEventListener('keydown', handleEsc)
        return () => {
            document.removeEventListener('keydown', handleEsc)
        }
    }, [finalOpen, onOpenChange, controlledOpen])

    const handleTriggerClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        const newOpen = !finalOpen
        onOpenChange?.(newOpen)
        if (controlledOpen === undefined) {
            setIsOpen(newOpen)
        }
    }

    const popout = finalOpen && content && (
        <PopoutContent
            ref={refs.setFloating}
            style={floatingStyles}
            className={className}
        >
            {content}
        </PopoutContent>
    )

    return (
        <>
            <div ref={referenceRef}>
                <div ref={refs.setReference} onClick={handleTriggerClick}>
                    {trigger}
                </div>
            </div>
            {popout && createPortal(popout, document.body)}
        </>
    )
}
